import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdDelete, MdWarning } from 'react-icons/md';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useLanguage } from '../../context/LanguageContext';

export default function DeleteDataModal({ sensorUUID, sensorName, targetUUID, targetName, type = 'sensor', onClose, onSuccess }) {
    const { t } = useLanguage();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);

    const uuid = targetUUID || sensorUUID;
    const name = targetName || sensorName || t('common.loading');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!startDate || !endDate) {
            return toast.error(t('deleteDataModal.selectDateRange'));
        }

        const targetTypeLabel = type === 'counter' ? t('deleteDataModal.counterLabel') : t('deleteDataModal.sensorLabel');
        const confirmMsg = t('deleteDataModal.confirmMessage', {
            name,
            type: targetTypeLabel,
            startDate,
            endDate
        });

        if (!window.confirm(confirmMsg)) return;

        try {
            setLoading(true);
            const endpoint = type === 'counter' ? `/counter-data/${uuid}` : `/sensor-data/${uuid}`;
            const res = await api.delete(endpoint, {
                params: { startDate, endDate }
            });

            toast.success(res.data.message || t('deleteDataModal.dataDeleted'));
            onSuccess();
        } catch (err) {
            console.error('Delete data error:', err);
            toast.error(err.response?.data?.message || t('deleteDataModal.deleteFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="modal-overlay" onClick={onClose}>
                <motion.div
                    className="modal"
                    onClick={e => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    style={{ maxWidth: 450 }}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <div className="modal-title-icon" style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)' }}>
                                <MdDelete />
                            </div>
                            {t('deleteDataModal.title')}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    <form onSubmit={handleSubmit} className="modal-body">
                        <div style={{
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                            padding: '12px 16px',
                            borderRadius: 12,
                            marginBottom: 20,
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start'
                        }}>
                            <MdWarning style={{ color: 'var(--warning)', fontSize: 20, flexShrink: 0, marginTop: 2 }} />
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                <strong style={{ color: 'var(--warning)' }}>{t('deleteDataModal.warning')}</strong>{' '}
                                {t('deleteDataModal.warningDesc', {
                                    type: type === 'counter' ? t('deleteDataModal.counterRecords') : t('deleteDataModal.sensorRecords')
                                })}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('deleteDataModal.startDate')}</label>
                            <input
                                type="date"
                                className="form-input"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('deleteDataModal.endDate')}</label>
                            <input
                                type="date"
                                className="form-input"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="modal-footer" style={{ marginTop: 24, padding: 0, border: 'none' }}>
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn btn-danger" disabled={loading} style={{ flex: 1 }}>
                                {loading ? <span className="spinner spinner-sm" /> : <MdDelete />}
                                {t('deleteDataModal.deletePermanently')}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
